/**
 * Dice rolling utility functions for the ChoiceRealms game system
 */

/**
 * Roll a single die with the specified number of sides
 * @param {number} sides - Number of sides on the die
 * @returns {number} Random roll between 1 and sides (inclusive)
 */
export const rollDice = (sides = 6) => {
  return Math.floor(Math.random() * sides) + 1;
};

/**
 * Roll multiple dice of the same type
 * @param {number} count - Number of dice to roll
 * @param {number} sides - Number of sides on each die
 * @returns {number[]} Array of individual die results
 */
export const rollMultipleDice = (count = 1, sides = 6) => {
  return Array(count).fill(0).map(() => rollDice(sides));
};

/**
 * Roll dice and sum the results (e.g., 3d6)
 * @param {number} count - Number of dice to roll
 * @param {number} sides - Number of sides on each die
 * @returns {number} Sum of all dice rolled
 */
export const rollDiceSum = (count = 1, sides = 6) => {
  return rollMultipleDice(count, sides).reduce((sum, roll) => sum + roll, 0);
};

/**
 * Roll 4d6 and drop the lowest die (common ability score generation method)
 * @returns {number} Sum of the highest 3 dice from 4d6
 */
export const roll4d6DropLowest = () => {
  const rolls = rollMultipleDice(4, 6);
  rolls.sort((a, b) => b - a); // Sort in descending order
  return rolls.slice(0, 3).reduce((sum, roll) => sum + roll, 0);
};

/**
 * Roll with advantage (roll twice, take the higher result)
 * @param {number} sides - Number of sides on the die
 * @returns {object} Object containing the chosen roll and both individual rolls
 */
export const rollWithAdvantage = (sides = 20) => {
  const roll1 = rollDice(sides);
  const roll2 = rollDice(sides);
  const chosen = Math.max(roll1, roll2);
  
  return {
    result: chosen,
    rolls: [roll1, roll2],
    type: 'advantage'
  };
};

/**
 * Roll with disadvantage (roll twice, take the lower result)
 * @param {number} sides - Number of sides on the die
 * @returns {object} Object containing the chosen roll and both individual rolls
 */
export const rollWithDisadvantage = (sides = 20) => {
  const roll1 = rollDice(sides);
  const roll2 = rollDice(sides);
  const chosen = Math.min(roll1, roll2);
  
  return {
    result: chosen,
    rolls: [roll1, roll2],
    type: 'disadvantage'
  };
};

/**
 * Parses and rolls a dice notation string (e.g., "2d6+3")
 * @param {string} notation - Dice notation string (e.g., "2d6+3", "1d20-2", "3d8")
 * @returns {object} Object containing the result and roll details
 */
export const rollDiceNotation = (notation) => {
  const regex = /(\d+)d(\d+)((?:[+-]\d+)?)/;
  const match = notation.match(regex);
  
  if (!match) {
    throw new Error(`Invalid dice notation: ${notation}`);
  }
  
  const count = parseInt(match[1], 10);
  const sides = parseInt(match[2], 10);
  const modifier = match[3] ? parseInt(match[3], 10) : 0;
  
  const rolls = rollMultipleDice(count, sides);
  const diceSum = rolls.reduce((sum, roll) => sum + roll, 0);
  const total = diceSum + modifier;
  
  return {
    result: total,
    rolls,
    diceSum,
    modifier,
    notation
  };
};

export default {
  rollDice,
  rollMultipleDice,
  rollDiceSum,
  roll4d6DropLowest,
  rollWithAdvantage,
  rollWithDisadvantage,
  rollDiceNotation
};